import { useMemo, Fragment, useContext } from "react"
import PropTypes from "prop-types"
import { map, omit, pick } from "lodash"
import { Divider } from "@mui/material"
import { createPeriodPerformanceSingleDateQuery, BATTING_TOTAL, BATTING_ALPHA, BATTING_FACTORS, SLUGGING_TOTAL, SLUGGING_ALPHA, SLUGGING_FACTORS } from "gatherers/single-date/period-performance"
import DataIntegrator from "gatherers/data-integrator"
import { smallNumberFormatter } from "helpers/formatters/numbers"
import { ONE_DAY } from "helpers/dates"
import { DECISION_QUALITY_LABELS } from "connected-components/compare/helpers/transform-decision-quality-labels"
import CompareSection from "connected-components/compare/renderers/compare-section"
import { useAnalytics } from "connected-components/analytics"
import { useBaseWithSegment } from "connected-components/dashboard/helpers"
import { KPIContext } from "../../context/kpi"
import { KPI_EVENTS } from "../../analytics"
import { addDeltaColumn } from "connected-components/shared/helpers/data-transforms"
import { isInstrumentIntradayCapable } from "../../helpers"

export default function KPIDecisions({ modelMetadata, context, instrumentsWithQueryOptions, instrumentsWithMetadata, positionSetsWithQueryOptions, positionSets, isIntradayMode, showDelta, getEffectiveEndDate, intradayPriceRefreshTime, instrumentsMetadata }) {
  const { sectionSettings, setSectionExpanded } = useContext(KPIContext)
  const { base, filter, normalization, performanceStartDate, performanceEndDate, period, isEndVsStartDateComparison, selectedMode, intradayBatchMetadata } = useContext(context)
  const analytics = useAnalytics()
  const baseWithSegment = useBaseWithSegment(base, filter, normalization)

  const apiStartDate = period === ONE_DAY ? performanceEndDate : performanceStartDate

  const modelName = useMemo(() => pick(modelMetadata, ["id"]), [modelMetadata])

  const decisionsSections = useMemo(
    () => [
      {
        id: "batting_total",
        name: "Batting - Total",
        tooltip: DECISION_QUALITY_LABELS.BATTING.tooltip,
        defaultExpanded: true,
        separator: true,
        metrics: map(positionSetsWithQueryOptions, (positionSet, index) => {
          const intradayEnabled = isInstrumentIntradayCapable(positionSets[index], instrumentsWithQueryOptions, instrumentsMetadata)
          return createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            positionSetsWithQueryOptions[0].queryPositionSet,
            apiStartDate,
            getEffectiveEndDate(index),
            [
              BATTING_TOTAL,
              BATTING_ALPHA,
              BATTING_FACTORS,
            ],
            { contextBase: baseWithSegment, isIntradayMode, intradayEnabled, intradayPriceRefreshTime, batchId: intradayBatchMetadata?.id, intradayBatchMetadata })
        }),
        rowCount: 2,
      },
      {
        id: "slugging_total",
        name: "Slugging - Total",
        tooltip: DECISION_QUALITY_LABELS.SLUGGING.tooltip,
        defaultExpanded: true,
        separator: true,
        metrics: map(positionSetsWithQueryOptions, (positionSet, index) => {
          const intradayEnabled = isInstrumentIntradayCapable(positionSets[index], instrumentsWithQueryOptions, instrumentsMetadata)
          return createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            positionSetsWithQueryOptions[0].queryPositionSet,
            apiStartDate,
            getEffectiveEndDate(index),
            [
              SLUGGING_TOTAL,
              SLUGGING_ALPHA,
              SLUGGING_FACTORS,
            ],
            { contextBase: baseWithSegment, isIntradayMode, intradayEnabled, intradayPriceRefreshTime, batchId: intradayBatchMetadata?.id, intradayBatchMetadata })
        }),
        rowCount: 2,
      },
    ],
    [positionSetsWithQueryOptions, modelName.id, apiStartDate, getEffectiveEndDate, baseWithSegment, isIntradayMode, intradayPriceRefreshTime, intradayBatchMetadata, instrumentsMetadata, instrumentsWithQueryOptions, positionSets],
  )

  return map(decisionsSections, (section, index) => <Fragment key={`decisions-${section.id}`}>
    <DataIntegrator
      dataTransform={showDelta ? (data) => addDeltaColumn(data, selectedMode) : undefined}
      metrics={section.metrics}
      Renderer={section.renderer || CompareSection}
      rendererProps={{
        queryState: sectionSettings?.[section.id] || { expanded: section.defaultExpanded, all: false },
        onQueryStateChange: (queryState) => {
          analytics.track(KPI_EVENTS.SECTION_TOGGLED, {
            sectionName: section.id,
            newState: queryState,
          })
          setSectionExpanded({ key: section.id, value: queryState })
        },
        index,
        formatter: smallNumberFormatter,
        configKey: `decisions-${section.id}`,
        columnCount: showDelta ? 3 : positionSets.length,
        positionSetsMetadata: positionSets,
        instrumentsWithQueryOptions,
        instrumentsWithMetadata,
        isEndVsStartDateComparison,
        useSkeletonLoader: true,
        buttonSize: "small",
        ...omit(section, ["id", "metrics", "renderer"]),
      }}
    />
    {section.separator && <Divider />}
  </Fragment>)
}

KPIDecisions.propTypes = {
  modelMetadata: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    missingDataFiles: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })),
    availability: PropTypes.shape({
      securitiesStartDate: PropTypes.string,
      currentDate: PropTypes.string,
      unmodeledDates: PropTypes.object,
    }),
  }).isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithQueryOptions: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
  }).isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  positionSetsWithQueryOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  positionSets: PropTypes.arrayOf(PropTypes.object).isRequired,
  isIntradayMode: PropTypes.bool.isRequired,
  showDelta: PropTypes.bool.isRequired,
  getEffectiveEndDate: PropTypes.func.isRequired,
}
